/**
 * 中间页面 - 入口页面
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Layout, Button, Carousel, Spin, Alert } from 'antd';
import { useNavigate } from 'react-router-dom';
import { carouselService, CarouselItem } from '../../../services/carouselService';
import { compressBase64Image, getBase64ImageSize, formatFileSize, preloadImage } from '../../../utils/imageUtils';
import FloatingEnterButton from '../../../components/FloatingEnterButton';
import './LandingPage.css';

const { Header, Content } = Layout;

const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const [carouselItems, setCarouselItems] = useState<CarouselItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [loadedImages, setLoadedImages] = useState<Set<number>>(new Set());
  const [compressedImages, setCompressedImages] = useState<Map<number, string>>(new Map());

  useEffect(() => {
    loadCarouselData();
  }, []);

  const loadCarouselData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const carouselRes = await carouselService.getEnabledList();
      
      if (carouselRes && Array.isArray(carouselRes) && carouselRes.length > 0) {
        // 按排序号排序
        const sorted = [...carouselRes].sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
        setCarouselItems(sorted);
      } else {
        // 如果没有数据，使用默认数据
        setCarouselItems([
          {
            id: 1,
            title: '现代化城市与自然和谐共存',
            subtitle: '黄石经济技术开发区·铁山区知识产权服务业集聚区',
            description: '壮丽的城市全景航拍图，展现日出日落时分城市与自然景观的完美融合，高楼大厦与翠绿山峦交织，体现现代化城市与自然和谐共存的壮丽景象',
            imageData: '/images/carousel/1.png',
            imageType: 'image/png',
            sortOrder: 1,
            status: '1'
          }
        ]);
      }
    } catch (error) {
      console.error('加载轮播图数据失败:', error);
      setError('加载轮播图数据失败，请稍后重试');
      // 使用默认数据
      setCarouselItems([
        {
          id: 1,
          title: '现代化城市与自然和谐共存',
          subtitle: '黄石经济技术开发区·铁山区知识产权服务业集聚区',
          description: '壮丽的城市全景航拍图，展现日出日落时分城市与自然景观的完美融合，高楼大厦与翠绿山峦交织，体现现代化城市与自然和谐共存的壮丽景象',
          imageData: '/images/carousel/1.png',
          imageType: 'image/png',
          sortOrder: 1,
          status: '1'
        }
      ]);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleEnterHome = () => {
    navigate('/show/main');
  };

  // 处理图片URL：确保Base64格式能正确显示，并添加压缩参数
  const getImageUrl = useCallback((item: CarouselItem) => {
    // 原始值去除首尾空白
    let raw = (item.imageData || '').trim();

    // 已是 dataURL 或 http(s) 绝对地址，直接返回（同时做一次 trim）
    if (!raw || raw.startsWith('data:') || raw.startsWith('http://') || raw.startsWith('https://')) {
      const url = raw;
      return url;
    }
    // 相对路径（静态资源）
    if (raw.startsWith('/')) {
      return raw;
    }

    // 处理已含有 dataURL 头但前面有空白或大小写差异等情况
    const maybeHeaderMatch = raw.match(/^data:[^,]+,(.*)$/i);
    if (maybeHeaderMatch) {
      raw = (maybeHeaderMatch[1] || '').trim();
    }

    // 去掉所有空白字符（防止“base64, 后多了空格”导致无法渲染）
    const cleanedBase64 = raw.replace(/\s+/g, '');
    const imageType = (item.imageType || 'image/jpeg').toLowerCase();
    const dataUrl = `data:${imageType};base64,${cleanedBase64}`;
    return dataUrl;
  }, []);

  // 获取优化后的图片URL（优先使用压缩版本）
  const getOptimizedImageUrl = useCallback((item: CarouselItem) => {
    if (item.id !== undefined) {
      const compressedUrl = compressedImages.get(item.id);
      if (compressedUrl) {
        return compressedUrl;
      }
    }
    return getImageUrl(item);
  }, [compressedImages, getImageUrl]);

  // 图片加载完成处理
  const handleImageLoad = useCallback((itemId: number) => {
    setLoadedImages(prev => new Set([...Array.from(prev), itemId]));
  }, []);

  // 图片加载失败处理
  const handleImageError = useCallback((itemId: number) => {
    // 可以设置默认图片
  }, []);

  // 压缩图片
  const compressImage = useCallback(async (item: CarouselItem) => {
    try {
      const originalUrl = getImageUrl(item);
      
      // 检查是否是Base64图片
      if (!originalUrl.startsWith('data:')) {
        return originalUrl;
      }
      
      // 检查图片大小
      const imageSize = getBase64ImageSize(originalUrl);
      
      // 如果图片小于500KB，不需要压缩
      if (imageSize < 500 * 1024) {
        return originalUrl;
      }
      
      // 压缩图片
      const compressedUrl = await compressBase64Image(originalUrl, 1920, 1080, 0.8);
      const compressedSize = getBase64ImageSize(compressedUrl);
      
      
      // 保存压缩后的图片
      if (item.id !== undefined) {
        const itemId = item.id;
        setCompressedImages(prev => new Map(prev).set(itemId, compressedUrl));
      }
      
      return compressedUrl;
    } catch (error) {
      return getImageUrl(item);
    }
  }, [getImageUrl]);

  return (
    <Layout className="landing-page">
      {/* 头部栏 - 只有项目Logo和项目名称 */}
      <Header className="landing-header">
        <div className="header-container">
          <div className="logo-title-box">
            <div className="logo-box">
              <img 
                src="/images/log.png" 
                alt="黄石经济技术开发区·铁山区知识产权服务业集聚区" 
                className="logo-img"
              />
            </div>
            <div className="title-box">
              <div className="title-main">黄石经济技术开发区·铁山区知识产权服务业集聚区</div>
              <div className="title-english">Huangshi Economic & Tech Development Zone · Tieshan District IP Service Cluster</div>
            </div>
          </div>
        </div>
      </Header>

      {/* 内容区域 - 轮播图背景 */}
      <Content className="landing-content">
        {loading ? (
          <div className="loading-container">
            <Spin size="large" />
            <p style={{ color: 'white', marginTop: '20px' }}>正在加载轮播图...</p>
          </div>
        ) : error ? (
          <div className="error-container">
            <Alert
              message="加载失败"
              description={error}
              type="error"
              showIcon
              action={
                <Button size="small" onClick={loadCarouselData}>
                  重试
                </Button>
              }
            />
          </div>
        ) : (
          (() => {
            const itemsToRender: CarouselItem[] = (carouselItems && carouselItems.length > 0)
              ? carouselItems
              : [{
                  id: 0,
                  title: '现代化城市与自然和谐共存',
                  subtitle: '黄石经济技术开发区·铁山区知识产权服务业集聚区',
                  description: '',
                  imageData: '/images/carousel/1.png',
                  imageType: 'image/png',
                  sortOrder: 1,
                  status: '1'
                }];
            return (
              <Carousel 
                autoplay 
                effect="fade" 
                className="landing-carousel"
              >
                {itemsToRender.map((item) => {
                  const imageUrl = getImageUrl(item);
                  return (
                    <div key={item.id || Math.random()} className="carousel-slide">
                    <div 
                      className="carousel-background"
                      style={{
                        backgroundImage: `url(${imageUrl})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        backgroundRepeat: 'no-repeat',
                        backgroundColor: '#f0f0f0',
                        height: 'calc(100vh - 80px)',
                        minHeight: 'calc(100vh - 80px)'
                      }}
                    >
                        {/* 可见图片层，确保部分浏览器/样式组合下也能正常显示 */}
                        <img
                          src={imageUrl}
                          alt={item.title}
                          style={{
                            position: 'absolute',
                            left: 0,
                            top: 0,
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            zIndex: 1
                          }}
                        />
                        <div className="carousel-overlay">
                          <div className="carousel-text">
                            <h1 className="main-title">{item.title}</h1>
                            {item.subtitle && <p className="sub-title">{item.subtitle}</p>}
                            {item.description && (
                              <p className="desc-text">{item.description}</p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </Carousel>
            );
          })()
        )}
        <FloatingEnterButton />
      </Content>
    </Layout>
  );
};

export default LandingPage;
